import { requireSupabaseClient } from "../../lib/supabase";
import type {
  MunicipalModuleSetting,
  MunicipalPosition,
  RolePermissionSummary,
  StaffAssignment,
  UserRole,
} from "../../types/domain";

export interface DirectoryPerson {
  id: string;
  fullName: string;
  student: boolean;
}

export interface RoleOption {
  id: string;
  code: UserRole;
  name: string;
  description: string;
}

export interface SavePositionInput {
  id?: string;
  departmentId: string | null;
  code: string;
  name: string;
  description: string;
  responsibilities: string[];
  leadership: boolean;
}

export interface SaveDepartmentInput {
  id?: string;
  code: string;
  name: string;
  shortName: string;
  description: string;
  location: string;
  schedule: string;
  phone: string;
  email: string;
  active: boolean;
  reason: string;
}

export interface SaveStaffAssignmentInput {
  userId: string;
  departmentId: string;
  positionId: string;
  role: UserRole;
  functionSummary: string;
  responsibilities: string[];
  authorityLimit: string;
  startsOn: string;
  endsOn: string | null;
  reason: string;
}

export interface SetModuleVisibilityInput {
  moduleCode: string;
  enabled: boolean;
  maintenanceMessage: string;
  reason: string;
}

type Relation = Record<string, unknown>;

function one(value: unknown): Relation {
  if (Array.isArray(value)) return (value[0] as Relation | undefined) ?? {};
  return value && typeof value === "object" ? (value as Relation) : {};
}

function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}

export async function listDirectoryPeople(
  municipalityId: string,
): Promise<DirectoryPerson[]> {
  const client = requireSupabaseClient();
  const [peopleResult, studentResult] = await Promise.all([
    client
      .from("profiles")
      .select("id, full_name")
      .eq("municipality_id", municipalityId)
      .order("full_name"),
    client
      .from("user_roles")
      .select("user_id, roles!inner(code)")
      .eq("municipality_id", municipalityId)
      .eq("roles.code", "student")
      .is("revoked_at", null),
  ]);
  if (peopleResult.error || studentResult.error) {
    throw new Error("No se pudo cargar el directorio municipal.");
  }
  const students = new Set(
    (studentResult.data ?? []).map((row) => String(row.user_id)),
  );
  return (peopleResult.data ?? []).map((row) => ({
    id: String(row.id),
    fullName: String(row.full_name),
    student: students.has(String(row.id)),
  }));
}

export async function setStudentAccess(input: {
  userId: string;
  enabled: boolean;
  reason: string;
}) {
  const { error } = await requireSupabaseClient().rpc("set_student_access", {
    p_user_id: input.userId,
    p_enabled: input.enabled,
    p_reason: input.reason,
  });
  if (error) {
    throw new Error(
      input.enabled
        ? "No se pudo habilitar a la persona como estudiante."
        : "No se pudo retirar el acceso de estudiante.",
    );
  }
}

export async function listRoleOptions(): Promise<RoleOption[]> {
  const { data, error } = await requireSupabaseClient()
    .from("roles")
    .select("id, code, name, description")
    .order("name");
  if (error) throw new Error("No se pudo cargar el catálogo de roles.");
  return (data ?? []).map((row) => ({
    id: String(row.id),
    code: String(row.code) as UserRole,
    name: String(row.name),
    description: String(row.description),
  }));
}

export async function saveDepartment(input: SaveDepartmentInput) {
  const { data, error } = await requireSupabaseClient().rpc("save_department", {
    p_department_id: input.id ?? null,
    p_code: input.code,
    p_name: input.name,
    p_short_name: input.shortName,
    p_description: input.description,
    p_location: input.location,
    p_schedule: input.schedule,
    p_phone: input.phone,
    p_email: input.email,
    p_active: input.active,
    p_reason: input.reason,
  });
  if (error) throw new Error("No se pudo guardar la dependencia municipal.");
  return data as unknown;
}

export async function listMunicipalPositions(
  municipalityId: string,
): Promise<MunicipalPosition[]> {
  const { data, error } = await requireSupabaseClient()
    .from("municipal_positions")
    .select(
      "id, municipality_id, department_id, code, name, description, responsibilities, is_leadership, active",
    )
    .eq("municipality_id", municipalityId)
    .eq("active", true)
    .order("display_order")
    .order("name");
  if (error) throw new Error("No se pudieron cargar los cargos municipales.");
  return (data ?? []).map((row) => ({
    id: String(row.id),
    municipalityId: String(row.municipality_id),
    departmentId: row.department_id ? String(row.department_id) : null,
    code: String(row.code),
    name: String(row.name),
    description: String(row.description ?? ""),
    responsibilities: Array.isArray(row.responsibilities)
      ? row.responsibilities.map(String)
      : [],
    leadership: Boolean(row.is_leadership),
    active: Boolean(row.active),
  }));
}

export async function listStaffAssignments(
  municipalityId: string,
): Promise<StaffAssignment[]> {
  const { data, error } = await requireSupabaseClient()
    .from("staff_assignments")
    .select(
      "id, user_id, department_id, position_id, role_id, function_summary, responsibilities, authority_limit, is_primary, status, starts_on, ends_on, profiles!staff_assignments_user_id_fkey(full_name), departments(name), municipal_positions(name), roles(code, name)",
    )
    .eq("municipality_id", municipalityId)
    .order("status")
    .order("created_at", { ascending: false });
  if (error) throw new Error("No se pudieron cargar las asignaciones del personal.");
  return (data ?? []).map((row) => {
    const profile = one(row.profiles);
    const department = one(row.departments);
    const position = one(row.municipal_positions);
    const role = one(row.roles);
    return {
      id: String(row.id),
      userId: String(row.user_id),
      fullName: text(profile.full_name) || "Usuario municipal",
      departmentId: String(row.department_id),
      departmentName: text(department.name),
      positionId: String(row.position_id),
      positionName: text(position.name),
      roleId: String(row.role_id),
      roleCode: text(role.code),
      roleName: text(role.name),
      functionSummary: String(row.function_summary ?? ""),
      responsibilities: Array.isArray(row.responsibilities)
        ? row.responsibilities.map(String)
        : [],
      authorityLimit: String(row.authority_limit ?? ""),
      primary: Boolean(row.is_primary),
      status: String(row.status) as StaffAssignment["status"],
      startsOn: String(row.starts_on),
      endsOn: row.ends_on ? String(row.ends_on) : null,
    };
  });
}

export async function listMunicipalModules(
  municipalityId: string,
): Promise<MunicipalModuleSetting[]> {
  const client = requireSupabaseClient();
  const [
    { data: catalog, error: catalogError },
    { data: settings, error: settingsError },
  ] = await Promise.all([
    client
      .from("module_catalog")
      .select("code, area, name, description, route, critical, display_order")
      .order("area")
      .order("display_order"),
    client
      .from("municipal_module_settings")
      .select("module_code, is_enabled, maintenance_message")
      .eq("municipality_id", municipalityId),
  ]);
  if (catalogError || settingsError) {
    throw new Error("No se pudo cargar la visibilidad de módulos.");
  }
  const byCode = new Map(
    (settings ?? []).map((row) => [String(row.module_code), row] as const),
  );
  return (catalog ?? []).map((row) => {
    const setting = byCode.get(String(row.code));
    return {
      code: String(row.code),
      area: String(row.area) as MunicipalModuleSetting["area"],
      name: String(row.name),
      description: String(row.description),
      route: String(row.route),
      critical: Boolean(row.critical),
      displayOrder: Number(row.display_order),
      enabled: setting ? Boolean(setting.is_enabled) : true,
      maintenanceMessage: setting ? String(setting.maintenance_message ?? "") : "",
    };
  });
}

export async function listRolePermissions(
  municipalityId: string,
): Promise<RolePermissionSummary[]> {
  const { data, error } = await requireSupabaseClient()
    .from("role_permissions")
    .select(
      "role_id, permission_code, effect, access_scope, roles(code, name), permission_definitions(name, risk_level)",
    )
    .eq("municipality_id", municipalityId)
    .order("permission_code");
  if (error) throw new Error("No se pudo cargar la matriz de permisos.");
  return (data ?? []).map((row) => {
    const role = one(row.roles);
    const permission = one(row.permission_definitions);
    return {
      roleId: String(row.role_id),
      roleCode: text(role.code),
      roleName: text(role.name),
      permissionCode: String(row.permission_code),
      permissionName: text(permission.name),
      riskLevel: text(permission.risk_level) as RolePermissionSummary["riskLevel"],
      scope: String(row.access_scope) as RolePermissionSummary["scope"],
      effect: String(row.effect) as RolePermissionSummary["effect"],
    };
  });
}

export async function saveMunicipalPosition(input: SavePositionInput) {
  const { data, error } = await requireSupabaseClient().rpc("save_municipal_position", {
    p_position_id: input.id ?? null,
    p_department_id: input.departmentId,
    p_code: input.code,
    p_name: input.name,
    p_description: input.description,
    p_responsibilities: input.responsibilities,
    p_is_leadership: input.leadership,
  });
  if (error) throw new Error("No se pudo guardar el cargo municipal.");
  return data as unknown;
}

export async function saveStaffAssignment(input: SaveStaffAssignmentInput) {
  const { data, error } = await requireSupabaseClient().rpc("save_staff_assignment", {
    p_user_id: input.userId,
    p_department_id: input.departmentId,
    p_position_id: input.positionId,
    p_role: input.role,
    p_function_summary: input.functionSummary,
    p_responsibilities: input.responsibilities,
    p_authority_limit: input.authorityLimit,
    p_starts_on: input.startsOn,
    p_ends_on: input.endsOn,
    p_reason: input.reason,
  });
  if (error) throw new Error("No se pudo guardar la asignación del personal.");
  return data as unknown;
}

export async function endStaffAssignment(id: string, reason: string) {
  const { error } = await requireSupabaseClient().rpc("end_staff_assignment", {
    p_assignment_id: id,
    p_reason: reason,
  });
  if (error) throw new Error("No se pudo finalizar la asignación.");
}

export async function setModuleVisibility(input: SetModuleVisibilityInput) {
  const { data, error } = await requireSupabaseClient().rpc("set_module_visibility", {
    p_module_code: input.moduleCode,
    p_enabled: input.enabled,
    p_maintenance_message: input.maintenanceMessage,
    p_reason: input.reason,
  });
  if (error) throw new Error("No se pudo actualizar la visibilidad del módulo.");
  return data as unknown;
}

export async function getPublicModuleVisibility(): Promise<Record<string, boolean>> {
  const { data, error } = await requireSupabaseClient().rpc(
    "get_public_module_visibility",
    { p_municipality_code: "PBY" },
  );
  if (error) throw error;
  const rows = data as unknown;
  if (!Array.isArray(rows)) return {};
  return Object.fromEntries(
    rows.flatMap((row) => {
      if (!row || typeof row !== "object") return [];
      const moduleCode = (row as { module_code?: unknown }).module_code;
      const enabled = (row as { is_enabled?: unknown }).is_enabled;
      return typeof moduleCode === "string" && typeof enabled === "boolean"
        ? [[moduleCode, enabled] as const]
        : [];
    }),
  );
}
